import CaseStudyMedia from './CaseStudyMedia'
import CaseStudyMediaCarousel from './CaseStudyMediaCarousel'
import { enrichCaseStudyCarouselItems, socialCardsToCarouselItems } from '../lib/socialCarouselCards'
import { PROJECT_TOPIC_LABEL_CLASS } from '../lib/projectTopicLabel'

const renderBlockText = (block, { showSideTitle = true } = {}) => (
  <div className="space-y-5">
    {showSideTitle && block.sideTitle ? (
      <h2 className={PROJECT_TOPIC_LABEL_CLASS}>{block.sideTitle.toLowerCase()}</h2>
    ) : null}

    {block.title ? (
      <h3 className={`${PROJECT_TOPIC_LABEL_CLASS} text-[11px]`}>{block.title.toLowerCase()}</h3>
    ) : null}

    {block.body?.map((paragraph) => (
      <p key={paragraph.slice(0, 32)} className="site-muted normal-case leading-relaxed">
        {paragraph}
      </p>
    ))}

    {block.bullets?.length ? (
      <ul className="space-y-2">
        {block.bullets.map((item) => (
          <li key={item} className="site-muted normal-case leading-relaxed">
            — {item}
          </li>
        ))}
      </ul>
    ) : null}

    {block.footer ? (
      <p className="site-muted normal-case leading-relaxed">{block.footer}</p>
    ) : null}
  </div>
)

const renderSectionIntro = (section) => (
  <>
    {section.body?.map((paragraph) => (
      <p key={paragraph.slice(0, 32)} className="site-muted normal-case leading-relaxed">
        {paragraph}
      </p>
    ))}

    {section.responsibilities?.length ? (
      <ul className="space-y-2">
        {section.responsibilities.map((item) => (
          <li key={item} className="site-muted normal-case leading-relaxed">
            — {item}
          </li>
        ))}
      </ul>
    ) : null}
  </>
)

const renderStackedMediaItems = (items = []) =>
  items.map((item) => (
    <div key={item.id || item.label}>
      {item.label ? <p className="site-label mb-3">{item.label.toLowerCase()}</p> : null}
      <CaseStudyMedia item={{ ...item, label: undefined }} />
    </div>
  ))

const hasBlockText = (block) =>
  Boolean(
    block.sideTitle ||
      block.title ||
      block.body?.length ||
      block.bullets?.length ||
      block.footer
  )

const renderSectionBlock = (block, aboutMedia = []) => {
  if (block.layout === 'stacked-media-side') return null

  const blockKey =
    block.title ||
    block.body?.[0]?.slice(0, 32) ||
    block.footer ||
    block.sideTitle ||
    block.media?.[0]?.id

  return (
    <div key={blockKey} className="space-y-8">
      {hasBlockText(block) ? renderBlockText(block) : null}

      {block.media?.length ? (
        block.mediaLayout === 'carousel' ? (
          <CaseStudyMediaCarousel
            items={enrichCaseStudyCarouselItems(block.media, aboutMedia)}
            showSocialStats={Boolean(block.showSocialStats)}
          />
        ) : block.media.length === 1 ? (
          <CaseStudyMedia item={block.media[0]} />
        ) : (
          <div className="grid gap-6 sm:grid-cols-2">
            {block.media.map((item) => (
              <CaseStudyMedia key={item.id || item.label} item={item} />
            ))}
          </div>
        )
      ) : null}
    </div>
  )
}

const renderSectionBlocks = (section, aboutMedia = []) => (
  <>
    {section.blocks?.map((block) => renderSectionBlock(block, aboutMedia))}
  </>
)

const ProjectCaseStudySections = ({
  hero,
  ipNote,
  about,
  aboutTitle,
  aboutMedia = [],
  socialCards = [],
  process,
  responsibilities = [],
  sections = [],
  contentTitle,
}) => {
  return (
    <div className="mx-auto w-full max-w-[90rem] space-y-10">
      {ipNote ? (
        <p className="site-muted normal-case leading-relaxed">{ipNote}</p>
      ) : null}

      {about ? (
        <section className="space-y-5">
          <h2 className={PROJECT_TOPIC_LABEL_CLASS}>{(aboutTitle || 'About').toLowerCase()}</h2>
          <p className="site-muted normal-case leading-relaxed">{about}</p>
        </section>
      ) : null}

      {socialCards.length ? (
        <CaseStudyMediaCarousel
          items={socialCardsToCarouselItems(socialCards, aboutMedia)}
          showSocialStats
          heading={contentTitle}
        />
      ) : null}

      {!socialCards.length && aboutMedia.length ? (
        <CaseStudyMediaCarousel items={aboutMedia} showSocialStats heading={contentTitle} />
      ) : null}

      {responsibilities.length || process ? (
        <section className="space-y-6 border-t border-border pt-8">
          {responsibilities.length ? (
            <div className="space-y-5">
              <h2 className={PROJECT_TOPIC_LABEL_CLASS}>role</h2>
              <ul className="space-y-2">
                {responsibilities.map((item) => (
                  <li key={item} className="site-muted normal-case leading-relaxed">
                    — {item}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {process ? (
            <div className="space-y-5">
              <h2 className={PROJECT_TOPIC_LABEL_CLASS}>process</h2>
              {(Array.isArray(process) ? process : [process]).map((paragraph) => (
                <p
                  key={paragraph.slice(0, 32)}
                  className="site-muted normal-case leading-relaxed"
                >
                  {paragraph}
                </p>
              ))}
            </div>
          ) : null}
        </section>
      ) : null}

      {sections.map((section, index) => {
        const followsProcess = Boolean(process) && index === 0

        if (section.layout === 'with-hero' && hero) {
          const stackedMediaBlock = section.blocks?.find(
            (block) => block.layout === 'stacked-media-side'
          )
          const stackedMediaItems = stackedMediaBlock?.media || []

          return (
            <section key={section.title} className="mb-12">
              <div className="flex flex-col gap-10 md:grid md:grid-cols-[42rem_minmax(0,1fr)] md:items-start md:gap-x-10">
                <div className="space-y-8">
                  <div>
                    {hero.label ? (
                      <p className={`${PROJECT_TOPIC_LABEL_CLASS} mb-4 text-2xl`}>{hero.label.toLowerCase()}</p>
                    ) : null}
                    <CaseStudyMedia item={{ ...hero, label: undefined }} />
                  </div>

                  {renderStackedMediaItems(stackedMediaItems)}
                </div>

                <div className="space-y-10 md:pt-20">
                  <div className="space-y-5">
                    {section.sideTitle ? (
                      <h2 className={PROJECT_TOPIC_LABEL_CLASS}>{section.sideTitle.toLowerCase()}</h2>
                    ) : null}
                    {renderSectionIntro(section)}
                  </div>

                  {stackedMediaBlock ? renderBlockText(stackedMediaBlock) : null}
                </div>
              </div>
            </section>
          )
        }

        return (
          <section
            key={section.title}
            className={[
              'space-y-8',
              followsProcess ? '' : 'border-t border-border pt-8',
            ].join(' ')}
          >
            <h2 className={PROJECT_TOPIC_LABEL_CLASS}>{section.title.toLowerCase()}</h2>

            {renderSectionIntro(section)}

            {section.media?.length ? (
              <div className="space-y-6">
                {section.media.map((item) => (
                  <CaseStudyMedia key={item.id || item.label} item={item} />
                ))}
              </div>
            ) : null}

            {renderSectionBlocks(section, aboutMedia)}
          </section>
        )
      })}
    </div>
  )
}

export default ProjectCaseStudySections
